import { zodResolver } from '@hookform/resolvers/zod';
import { FC, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { resetPassword } from '@/api/auth/auth.actions';
import Button from '@/components/controls/Button';
import {
  defaultInputStyle,
  defaultLabelStyle,
} from '@/components/controls/CustomEntityFormField.tsx';
import CustomInput from '@/components/controls/CustomInput';
import PasswordEyeIcon from '@/components/controls/images/input/PasswordEyeIcon.tsx';
import { ConfirmPasswordSchema } from '@/components/zod/types';

type ResetPasswordData = {
  password: string;
  confirmPassword: string;
};

const ResetPasswordForm: FC = () => {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordData>({
    resolver: zodResolver(ConfirmPasswordSchema),
  });

  const router = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState<boolean>(false);

  useEffect(() => {
    if (!token) {
      return;
    }

    localStorage.setItem('token', token);
  }, [token]);

  const onSubmit = async (data: ResetPasswordData): Promise<void> => {
    const response = await resetPassword(data.password);

    if (!response.success) {
      toast.error(response.error.message);
    }

    toast.success('Password successfully reset');

    localStorage.removeItem('token');

    setTimeout(() => {
      router('/sign-in');
    }, 1000);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="flex flex-col gap-5 self-center w-[calc(100vw-2rem)] max-w-[400px] md:max-w-[450px] fluid-desktop:max-w-[500px] xl:max-w-[650px] fluid-desktop:w-[40vw] xl:w-[30vw] min-w-0 nest-hub-form-width">
        <CustomInput
          name="password"
          type={!showPassword ? 'password' : 'text'}
          hasIcon
          label="Password"
          inputStyle={defaultInputStyle}
          labelStyle={defaultLabelStyle}
          icon={<PasswordEyeIcon />}
          onIconClick={() => {
            setShowPassword((prev) => !prev);
          }}
          control={control}
          error={errors.password}
        />
        <CustomInput
          name="confirmPassword"
          type={!showConfirmPassword ? 'password' : 'text'}
          hasIcon
          label="Confirm Password"
          inputStyle={defaultInputStyle}
          labelStyle={defaultLabelStyle}
          icon={<PasswordEyeIcon />}
          onIconClick={() => {
            setShowConfirmPassword((prev) => !prev);
          }}
          control={control}
          error={errors.confirmPassword}
        />
        <Button
          buttonStyle="justify-center items-center px-6 py-2 mt-5 text-base font-semibold text-white whitespace-nowrap bg-honoursOrange rounded-xl max-md:px-5 w-full text-lg"
          text="Reset Password"
          buttonType={{ type: 'submit' }}
          hasImage={false}
        />
      </div>
    </form>
  );
};

export default ResetPasswordForm;
